import { useState, useEffect } from 'react';
import { i18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Tooltip } from '@thermon-platforms/thermon-ui-core';
import useAlarmsStore from 'store/alarms';
import useAlarmTypesStore from 'store/alarm-types';
import useDevicesStore from 'store/devices';
import useDeviceTypesStore from 'store/device-types';
import Notes from 'components/notes';
import api from 'util/api';


function AlarmDetailModal({device, alarm, showNotes=false, close, dismiss}) {
  const [alarmRef, setAlarmRef] = useState(alarm);
  const [processing, setProcessing] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [cleared, setCleared] = useState(false);
  const [, alarmsStoreActions] = useAlarmsStore();
  const [alarmTypesStore] = useAlarmTypesStore();
  const [devicesStore] = useDevicesStore();
  const [deviceTypesStore] = useDeviceTypesStore();
  const model = devicesStore.shallowDevices.get(device.id);
  const controller = device.type == 'Controller' ? device : model.path.find(d => d.type == 'Controller');
  const controllerModel = deviceTypesStore.types.find(type => type.id == controller?.deviceModel);
  const showClear = controllerModel?.showClear;
  const type = alarmTypesStore.types.find(t => t.name == alarm.alarmType);
  const reading = type ? alarm.readings.find(r => r.readingType.toLowerCase() == type.parameter) : undefined;

  let queryInterval;
  useEffect(() => {
    setAlarmRef(alarm);
    queryInterval = setInterval(() => {
      api(`/alarms/${alarm.alarmId}`)
        .then(res => res.json())
        .then((res) => {
          setAlarmRef(res[0]);
        })
        .catch(() => {/* no-op */});
    }, 30 * 1000);

    return () => clearInterval(queryInterval);
  }, [alarm]);

  function acknowledge() {
    setProcessing(true);
    alarmsStoreActions
      .acknowledge(alarm)
      .then(res => {
        setAlarmRef(res);
      })
      .catch(() => {/* no-op */})
      .finally(() => {
        setProcessing(false);
      });
  }

  function clear() {
    setClearing(true);
    alarmsStoreActions
      .clear(alarm)
      .then(res => {
        setCleared(true);
      })
      .catch(() => {/* no-op */})
      .finally(() => {
        setClearing(false);
      });
  }

  return (
    <I18nProvider i18n={i18n}>
      <header>
        <h3><strong>{device?.name}</strong>: {type?.displayName || device?.type}{alarm.deviceIndex > 0 ? ` (${alarm.deviceIndex})` : ''}</h3>
      </header>
      <section>
        <ul className="alarm-detail">
          {reading ? <li>
            <strong><Trans>Value</Trans></strong>:&nbsp;
            {reading.value}{reading.unit?.replace('deg', '°')}
          </li> : <></>}
          <li>
            <strong><Trans>Created</Trans></strong>:&nbsp;
            {new Date(alarmRef.createdAt).toLocaleString()}
          </li>
          <li>
            <strong><Trans>Last Updated</Trans></strong>:&nbsp;
            {new Date(alarmRef.lastUpdatedAt).toLocaleString()}
          </li>
          <li>
            <strong><Trans>Acknowledged</Trans></strong>:&nbsp;
            { !alarmRef.acknowledged ?
              'No' :
              <>
                {alarmRef.acknowledgedDetails ?
                  <>
                    {new Date(alarmRef.acknowledgedDetails?.acknowledgedAt).toLocaleString()}&nbsp;
                    by {alarmRef.acknowledgedDetails.acknowledgedBy}
                  </>
                : 'Yes'}
              </>
            }
          </li>
          {showNotes ? <li className="notes">
            <Notes device={device} />
          </li> : null}
        </ul>
      </section>
      <footer>
        {showClear && !cleared ?
          <button
            onClick={clear}
            disabled={clearing}
            className={clearing ? 'processing' : ''}
            data-tooltip-content={t`Requests to clear this alarm may not succeed if the underlying issue persists.`}
            data-tooltip-id="alarm-detail-tooltip"
          >
            <Trans>Clear</Trans>
          </button>
        : <></>}
        {!alarmRef.acknowledged ?
          <button
            onClick={acknowledge}
            disabled={processing || alarmRef.alarmState == 'Acknowledging'}
            className={processing || alarmRef.alarmState == 'Acknowledging' ? 'processing' : ''}
          >
            <Trans>Acknowledge</Trans>
          </button>
        : <></>}
        <button onClick={dismiss}><Trans>Close</Trans></button>
      </footer>
      <Tooltip id="alarm-detail-tooltip" />
    </I18nProvider>
  )
}

export default AlarmDetailModal;
